import { HTMLAttributes, ReactNode, Ref, RefObject } from 'react'
import { Backdrop, Modal } from '@mui/material'
import { motion, MotionConfig } from 'motion/react'
import { backdropTransitionDuration } from './menuAnimation'
import usePanelMorph from './usePanelMorph'

// The Modal treats the panel as its transition (in, onEnter, onExited),
// so it stays visible until the panel has shrunk back into the menu button
const SideMenuPanel = (
  props: HTMLAttributes<HTMLDivElement> & {
    in: boolean
    buttonRef: RefObject<HTMLButtonElement | null>
    onEnter?: () => void
    onExited?: () => void
    ref?: Ref<HTMLDivElement>
  },
) => {
  const { in: isOpen, buttonRef, onEnter, onExited, children, ...wrapperProps } = props
  const { panelRef, controls, variants } = usePanelMorph({
    isOpen,
    buttonRef,
    onOpenStart: onEnter,
    onCloseEnd: onExited,
  })

  return (
    <MotionConfig reducedMotion="user">
      <div
        {...wrapperProps}
        className="fixed left-0 top-0 z-20 w-full outline-none drop-shadow-floating short-sm:h-full xs:tall-sm:top-[17vh] xs:tall-sm:w-[420px] sm:top-[8vh] xl:w-[520px] xxl:top-[13vh]"
      >
        <motion.div
          ref={panelRef}
          variants={variants}
          initial="collapsed"
          animate={controls}
          className="flex h-full flex-col py-12 pl-8 pr-12 font-approach-light text-[8px] text-lightGrey short-sm:py-6 xs:tall-sm:rounded-r-[17px] xs:tall-sm:pl-12 xs:tall-sm:pr-[72px] xs:tall-sm:pt-10 md:text-[9px] xl:pl-16 xl:pr-24 xl:text-[12px]"
        >
          {children}
        </motion.div>
      </div>
    </MotionConfig>
  )
}

const SideMenuModal = (props: {
  children: ReactNode
  isOpen: boolean
  onClose: () => void
  buttonRef: RefObject<HTMLButtonElement | null>
}) => (
  <Modal
    open={props.isOpen}
    onClose={props.onClose}
    keepMounted
    closeAfterTransition
    slots={{ backdrop: Backdrop }}
    slotProps={{
      backdrop: {
        style: { backgroundColor: 'rgb(255, 255, 255, 0.75)' },
        transitionDuration: backdropTransitionDuration,
      },
    }}
  >
    <SideMenuPanel in={props.isOpen} buttonRef={props.buttonRef}>
      {props.children}
    </SideMenuPanel>
  </Modal>
)

export default SideMenuModal
